import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Textarea, Checkbox } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { FileText, UserPlus, Building2, Trash2 } from 'lucide-react';
import type { CustomerDetail } from '@/api/customers';
import { useConvertCustomer, useDeleteCustomer, useToggleGreyMarket } from '@/api/customer-basic';
import { AddNoteDialog } from './add-note-dialog';
import { AddContactDialog } from './add-contact-dialog';
import { NumericInput } from '@/components/numeric-input';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

/**
 * Row of customer detail actions with their modals: Add Note, Add Contact,
 * Convert to Company (needs a target company id), Delete (only for dead leads),
 * and the Special-customer (grey-market) toggle. Each mutation surfaces errors
 * in-dialog and shows a loading state on its confirm button.
 */
export function CustomerActionButtons({ customer }: { customer: CustomerDetail }) {
  const navigate = useNavigate();
  const id = customer.customer_id;

  const [noteOpen, setNoteOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [reason, setReason] = useState('');

  const convert = useConvertCustomer(id);
  const del = useDeleteCustomer(id);
  const greyMarket = useToggleGreyMarket(id);

  const hasCompany = !!customer.company?.company_id;
  const canDelete = customer.customer_status === 'dead-lead';

  const submitConvert = () => {
    const cid = Number(companyId);
    if (!cid) return;
    convert.mutate(cid, {
      onSuccess: () => { setConvertOpen(false); setCompanyId(''); convert.reset(); },
    });
  };

  const submitDelete = () => {
    if (!reason.trim()) return;
    del.mutate(reason.trim(), { onSuccess: () => navigate('/customers') });
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-3)', alignItems: 'center', justifyContent: 'space-between' }}>
      {/* Special-customer flag */}
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: greyMarket.isPending ? 'wait' : 'pointer', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        <Checkbox
          checked={!!customer.is_grey_market}
          disabled={greyMarket.isPending}
          onCheckedChange={() => greyMarket.mutate()}
        />
        <span>Special customer — invoices sent without company logo</span>
      </label>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
        <Button variant="secondary" startIcon={<FileText size={16} />} onClick={() => setNoteOpen(true)}>Add note</Button>
        <Button variant="secondary" startIcon={<UserPlus size={16} />} onClick={() => setContactOpen(true)}>Add contact</Button>
        <Button variant="secondary" startIcon={<Building2 size={16} />} disabled={hasCompany} onClick={() => setConvertOpen(true)}>Convert to company</Button>
        <Button variant="danger" startIcon={<Trash2 size={16} />} disabled={!canDelete} onClick={() => setDeleteOpen(true)}>Delete customer</Button>
      </div>

      <AddNoteDialog customerId={id} open={noteOpen} onOpenChange={setNoteOpen} />
      <AddContactDialog customerId={id} open={contactOpen} onOpenChange={setContactOpen} />

      {/* Convert to company */}
      <ConfirmDialog
        open={convertOpen}
        onOpenChange={(o) => { if (!convert.isPending) { setConvertOpen(o); if (!o) { setCompanyId(''); convert.reset(); } } }}
        title="Convert to company"
        description={`Link "${customer.customer_name}" to a company account. Enter the target company's ID.`}
        confirmLabel="Convert"
        loading={convert.isPending}
        onConfirm={submitConvert}
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {convert.isError ? <Alert tone="danger" title="Could not convert">{errorMessage(convert.error)}</Alert> : null}
          <Field label="Company ID" required>
            {(p) => <NumericInput thousandSeparator={false} precision={0} {...p} placeholder="e.g. 1024" value={companyId} onChange={(e) => setCompanyId(e.target.value)} />}
          </Field>
        </div>
      </ConfirmDialog>

      {/* Delete customer */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(o) => { if (!del.isPending) { setDeleteOpen(o); if (!o) { setReason(''); del.reset(); } } }}
        title="Delete customer"
        description={`This soft-deletes "${customer.customer_name}". A reason is required.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={submitDelete}
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {del.isError ? <Alert tone="danger" title="Could not delete">{errorMessage(del.error)}</Alert> : null}
          <Field label="Deletion reason" required>
            {(p) => <Textarea {...p} rows={3} placeholder="Why is this customer being deleted?" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
        </div>
      </ConfirmDialog>
    </div>
  );
}

export default CustomerActionButtons;
